import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTenantId } from './useTenantId';

export interface ContractDocument {
  id: string;
  company_id: string;
  tenant_id: string;
  document_type: string;
  document_category: string;
  file_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  document_number?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  verification_status: 'pending' | 'verified' | 'rejected' | 'expired';
  verified_by_user_id?: string;
  verified_at?: string;
  verification_notes?: string;
  contract_parties?: string;
  contract_value?: number;
  contract_status?: string;
  contract_description?: string;
  auto_renewal?: boolean;
  created_at: string;
  updated_at: string;
  company?: {
    id: string;
    name: string;
    company_type: string;
  };
}

export interface UploadContractData {
  company_id: string;
  file: File;
  document_type: string;
  contract_parties?: string;
  contract_value?: number;
  contract_description?: string;
  expiry_date?: string;
  auto_renewal?: boolean;
}

export function useContractDocuments(companyId?: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantId();

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: companyId ? ['contracts', tenantId, companyId] : ['contracts', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      let query = supabase
        .from('partner_documents')
        .select(`
          *,
          company:companies(id, name, company_type)
        `)
        .eq('tenant_id', tenantId)
        .eq('document_category', 'contract');

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as ContractDocument[];
    },
    enabled: !!tenantId,
  });

  const uploadContract = useMutation({
    mutationFn: async (data: UploadContractData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      if (!tenantId) throw new Error('No tenant context');

      // Generate unique file path
      const timestamp = Date.now();
      const filePath = `${tenantId}/${data.company_id}/contracts/${timestamp}-${data.file.name}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('partner-documents')
        .upload(filePath, data.file);

      if (uploadError) throw uploadError;

      // Create database record
      const insertData: any = {
        company_id: data.company_id,
        tenant_id: tenantId,
        document_type: data.document_type,
        document_category: 'contract',
        file_path: filePath,
        file_name: data.file.name,
        file_size_bytes: data.file.size,
        mime_type: data.file.type,
        is_required_for_prequalification: false,
        uploaded_by_user_id: user.id,
        verification_status: 'pending',
        contract_status: 'active',
        contract_parties: data.contract_parties,
        contract_value: data.contract_value,
        contract_description: data.contract_description,
        expiry_date: data.expiry_date,
        auto_renewal: data.auto_renewal || false,
      };

      const { data: contract, error: dbError } = await supabase
        .from('partner_documents')
        .insert(insertData)
        .select()
        .single();

      if (dbError) throw dbError;
      return contract;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', tenantId] });
      toast.success('Contract uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload contract');
    },
  });

  const updateContract = useMutation({
    mutationFn: async ({ contractId, updates }: { contractId: string; updates: Partial<ContractDocument> }) => {
      const { data, error } = await supabase
        .from('partner_documents')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', contractId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', tenantId] });
      toast.success('Contract updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update contract');
    },
  });

  const deleteContract = useMutation({
    mutationFn: async (contractId: string) => {
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) throw new Error('Contract not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('partner-documents')
        .remove([contract.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('partner_documents')
        .delete()
        .eq('id', contractId);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', tenantId] });
      toast.success('Contract deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete contract');
    },
  });

  const downloadContract = useMutation({
    mutationFn: async (contractId: string) => {
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) throw new Error('Contract not found');

      const { data, error } = await supabase.storage
        .from('partner-documents')
        .createSignedUrl(contract.file_path, 300);

      if (error) throw error;
      
      window.open(data.signedUrl, '_blank');
      return data.signedUrl;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to download contract');
    },
  });

  // Computed stats
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.contract_status === 'active').length,
    expiringIn30Days: contracts.filter(c => {
      if (!c.expiry_date) return false;
      const expiry = new Date(c.expiry_date);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
    }).length,
    expiringIn90Days: contracts.filter(c => {
      if (!c.expiry_date) return false;
      const expiry = new Date(c.expiry_date);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 90;
    }).length,
    expired: contracts.filter(c => {
      if (!c.expiry_date) return false;
      return new Date(c.expiry_date) < new Date();
    }).length,
  };

  return {
    contracts,
    isLoading,
    stats,
    uploadContract: uploadContract.mutateAsync,
    updateContract: updateContract.mutateAsync,
    deleteContract: deleteContract.mutateAsync,
    downloadContract: downloadContract.mutateAsync,
    isUploading: uploadContract.isPending,
  };
}
